import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProjectSummary = ({ formData, onEdit, onCreateProject, isCreating, errors }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOnlineOnly = formData?.basics?.isOnlineOnly;

  const formatBudgetRange = (range) => {
    if (!range) return 'Not specified';
    return range?.replace('-', ' - $')?.replace('000', ',000')?.replace('0000', '0,000');
  };

  const formatTimeline = (timeline) => {
    if (!timeline) return 'Not specified';
    return timeline?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const formatDeadline = (deadline, customDeadline) => {
    if (deadline === 'custom' && customDeadline) {
      return new Date(customDeadline)?.toLocaleDateString();
    }
    if (deadline) {
      return deadline?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
    }
    return 'Not specified';
  };

  const formatRequirements = (requirements) => {
    if (!requirements || requirements?.length === 0) return 'None specified';
    return requirements?.map(req => 
      req?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())
    )?.join(', ');
  };

  const getPurchaseTypeLabel = (type) => {
    const labels = {
      'car': 'Vehicle/Car',
      'home': 'Home Purchase',
      'refinancing': 'Home Refinancing',
      'furniture': 'Furniture & Home Goods',
      'equipment': 'Business Equipment',
      'other': 'Other'
    };
    return labels?.[type] || type;
  };

  const renderSpecifications = () => {
    const specs = formData?.specs || {};
    const filledSpecs = Object.entries(specs)
      ?.filter(([key, value]) => key !== 'weightings' && value != null && String(value)?.trim() !== '')
      ?.map(([key, value]) => ({
        label: key?.replace(/([A-Z])/g, ' $1')?.replace(/^./, s => s?.toUpperCase())?.trim(),
        value: String(value),
      }));

    if (filledSpecs?.length === 0) {
      return <span className="text-text-secondary">No specifications provided</span>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filledSpecs?.map(({ label, value }) => (
          <div key={label}>
            <span className="font-medium text-foreground">{label}:</span>
            <span className="text-text-secondary ml-1">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Get the appropriate data source and step index based on project type
  const getVendorData = () => {
    if (isOnlineOnly) {
      return {
        data: formData?.options || [],
        title: 'Purchase Options',
        stepIndex: 3,
        icon: 'ShoppingCart'
      };
    } else {
      return {
        data: formData?.vendors || [],
        title: 'Vendors',
        stepIndex: 3,
        icon: 'Users'
      };
    }
  };

  const vendorData = getVendorData();

  // Check if project can be created (no validation restrictions for online-only projects)
  const canCreateProject = () => {
    if (isOnlineOnly) {
      // Online-only projects can always be created, regardless of options count
      return true;
    } else {
      // Regular projects require at least one vendor
      return formData?.vendors && formData?.vendors?.length > 0;
    }
  };

  const getCreateButtonText = () => {
    if (isOnlineOnly) {
      return 'Create Stack & Save Options';
    } else {
      return 'Create Stack & Send Requests';
    }
  };

  const getProjectDescription = () => {
    if (isOnlineOnly) {
      return 'Review your stack details before creating your online purchase stack.';
    } else {
      return 'Review your stack details before creating and sending quote requests to vendors.';
    }
  };

  const getWarningMessage = () => {
    if (isOnlineOnly) {
      // No warning message for online-only projects - they can proceed without options
      return null;
    } else {
      return 'Please add at least one vendor before creating the stack.';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-foreground">Stack Summary</h3>
          <button
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
        <p className="text-text-secondary mb-6">{getProjectDescription()}</p>

        {/* Primary CTA - Front and Center */}
        <div className="flex flex-col items-center justify-center py-8 px-6 bg-gradient-to-b from-surface to-card rounded-xl border border-border">
          <Icon name={isOnlineOnly ? 'ShoppingCart' : 'Send'} size={40} className="text-primary mb-4 opacity-80" />
          <h4 className="text-lg font-semibold text-foreground mb-2">Ready to create your stack?</h4>
          <p className="text-sm text-text-secondary mb-6 text-center max-w-sm">
            {isOnlineOnly
              ? 'Your purchase options are saved and ready for comparison.'
              : 'Your stack is ready. Create it to send quote requests to vendors.'}
          </p>
          <Button
            variant="default"
            size="lg"
            onClick={onCreateProject}
            disabled={!canCreateProject() || isCreating}
            loading={isCreating}
            iconName={isOnlineOnly ? 'BarChart2' : 'Send'}
            iconPosition="right"
            className="w-full max-w-xs"
          >
            {getCreateButtonText()}
          </Button>

          {/* Warning for regular stacks without vendors */}
          {!canCreateProject() && getWarningMessage() && (
            <div className="mt-4 text-center text-sm text-warning bg-warning/10 rounded p-3 border border-warning/20 w-full max-w-xs">
              <Icon name="AlertTriangle" size={16} className="inline mr-2" />
              {getWarningMessage()}
            </div>
          )}

          {/* Info for online-only */}
          {isOnlineOnly && (
            <p className="mt-3 text-xs text-text-secondary text-center">
              {vendorData?.data?.length > 0
                ? `${vendorData?.data?.length} purchase option(s) will be saved with your stack.`
                : 'You can add purchase options after creating your stack.'}
            </p>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-6">
          {/* Project Basics */}
          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-foreground flex items-center">
                <Icon name="FileText" size={16} className="mr-2" />
                Stack Information
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(0)}
                iconName="Edit2"
                iconPosition="left"
              >
                Edit
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-foreground">Stack Name:</span>
                <p className="text-text-secondary mt-1">{formData?.basics?.projectName || 'Not specified'}</p>
              </div>
              <div>
                <span className="font-medium text-foreground">Purchase Type:</span>
                <p className="text-text-secondary mt-1">{getPurchaseTypeLabel(formData?.basics?.purchaseType)}</p>
              </div>
              <div>
                <span className="font-medium text-foreground">Budget Range:</span>
                <p className="text-text-secondary mt-1">${formatBudgetRange(formData?.basics?.budgetRange)}</p>
              </div>
              <div>
                <span className="font-medium text-foreground">Timeline:</span>
                <p className="text-text-secondary mt-1">{formatTimeline(formData?.basics?.timeline)}</p>
              </div>
              {formData?.basics?.notes && (
                <div className="md:col-span-2">
                  <span className="font-medium text-foreground">Notes:</span>
                  <p className="text-text-secondary mt-1">{formData?.basics?.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-foreground flex items-center">
                <Icon name="Settings" size={16} className="mr-2" />
                Specifications
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(1)}
                iconName="Edit2"
                iconPosition="left"
              >
                Edit
              </Button>
            </div>

            <div className="text-sm">
              {renderSpecifications()}
            </div>
          </div>

          {/* Vendors/Options */}
          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-foreground flex items-center">
                <Icon name={vendorData?.icon} size={16} className="mr-2" />
                {vendorData?.title} ({vendorData?.data?.length || 0})
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(vendorData?.stepIndex)}
                iconName="Edit2"
                iconPosition="left"
              >
                Edit
              </Button>
            </div>

            {vendorData?.data && vendorData?.data?.length > 0 ? (
              <div className="space-y-3">
                {vendorData?.data?.map((item, index) => (
                  <div key={item?.id || index} className="flex items-start justify-between p-3 bg-surface rounded border border-border gap-3 min-w-0">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{item?.name || item?.retailer}</p>
                      <p className="text-sm text-text-secondary break-all line-clamp-2">
                        {isOnlineOnly ? (item?.url || item?.website) : item?.email}
                      </p>
                    </div>
                    <div className="text-sm text-text-secondary flex-shrink-0">
                      {isOnlineOnly ? (
                        item?.price && <p className="font-medium text-foreground">{item?.price}</p>
                      ) : (
                        item?.phone && <p>{item?.phone}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-secondary text-sm">
                {isOnlineOnly ? 'No purchase options added yet' : 'No vendors added yet'}
              </p>
            )}
          </div>

          {/* Quote Settings - Only show for regular stacks */}
          {!isOnlineOnly && (
            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-foreground flex items-center">
                  <Icon name="MessageSquare" size={16} className="mr-2" />
                  Quote Request Settings
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(4)}
                  iconName="Edit2"
                  iconPosition="left"
                >
                  Edit
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-foreground">Response Deadline:</span>
                  <p className="text-text-secondary mt-1">
                    {formatDeadline(formData?.quoteSettings?.deadline, formData?.quoteSettings?.customDeadline)}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-foreground">Required Information:</span>
                  <p className="text-text-secondary mt-1">
                    {formatRequirements(formData?.quoteSettings?.requirements)}
                  </p>
                </div>
                {formData?.quoteSettings?.instructions && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-foreground">Special Instructions:</span>
                    <p className="text-text-secondary mt-1">{formData?.quoteSettings?.instructions}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {errors && Object.keys(errors)?.length > 0 && (
            <div className="rounded-lg p-4 bg-red-50 border border-red-200 space-y-2">
              <div className="flex items-center gap-2 text-red-700 font-medium text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>Please fix the following before creating your stack:</span>
              </div>
              <ul className="list-disc list-inside space-y-1">
                {Object.values(errors)?.map((msg, i) => (
                  <li key={i} className="text-sm text-red-600">{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectSummary;